import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { getClinicUsers } from "../../apis/user";
import { createLeaveRequest } from "../../apis/leaveRequest";

interface RequestLeaveModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const RequestLeaveModal: React.FC<RequestLeaveModalProps> = ({ onClose, onSuccess }) => {
  const { showNotification } = useNotification();
  const [clinicUsers, setClinicUsers] = useState<any[]>([]);
  const [selectedClinicUserId, setSelectedClinicUserId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [startHalf, setStartHalf] = useState<string>("");
  const [endHalf, setEndHalf] = useState<string>("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const data = res?.data ?? res;
      const users = Array.isArray(data) ? data : data?.data ?? [];
      setClinicUsers(users);
      const userObj = window.localStorage.getItem("user");
      if (userObj) {
        const user = JSON.parse(userObj);
        const clinicUserId = user.clinicUserId ?? user.clinic_user_id;
        if (clinicUserId && users.some((u: any) => u.id === clinicUserId)) {
          setSelectedClinicUserId(clinicUserId);
        }
      }
    });
  }, []);

  const handleSubmit = async () => {
    if (!selectedClinicUserId) {
      showNotification("warning", "Please select the staff member", "", "topRight");
      return;
    }
    if (startDate > endDate) {
      showNotification("warning", "End date must be on or after start date", "", "topRight");
      return;
    }
    setSaving(true);
    try {
      await createLeaveRequest({
        clinicUserId: selectedClinicUserId,
        startDate,
        endDate,
        startHalf: startHalf || undefined,
        endHalf: endHalf || undefined,
        reason: reason.trim() || undefined,
      });
      showNotification("success", "Leave request submitted successfully", "", "topRight");
      onSuccess();
      onClose();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to submit leave request",
        err?.response?.data?.message || "",
        "topRight"
      );
    } finally {
      setSaving(false);
    }
  };

  const getClinicUserLabel = (u: any) => {
    const name = `${u.firstName ?? u.first_name ?? ""} ${u.lastName ?? u.last_name ?? ""}`.trim();
    return name || u.email || `Clinic User #${u.id}`;
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select staff"
          value={selectedClinicUserId ?? undefined}
          onChange={(v) => setSelectedClinicUserId(v)}
          options={clinicUsers.map((u: any) => ({
            value: u.id,
            label: getClinicUserLabel(u),
          }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Half (optional)</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Full day / AM / PM"
            value={startHalf || undefined}
            onChange={(v) => setStartHalf(v ?? "")}
            allowClear
            options={[
              { value: "AM", label: "AM" },
              { value: "PM", label: "PM" },
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Half (optional)</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Full day / AM / PM"
            value={endHalf || undefined}
            onChange={(v) => setEndHalf(v ?? "")}
            allowClear
            options={[
              { value: "AM", label: "AM" },
              { value: "PM", label: "PM" },
            ]}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason for leave"
          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Submitting..." : "Request Leave"}
        </button>
      </div>
    </div>
  );
};

export default RequestLeaveModal;
